import { AxiosResponse } from "axios";
import { PickupStatus } from "../../type/entities/pickup-type";
import { RestServiceConnection } from "../restServiceConnection";

const PICKUPS_ENDPOINT = "/pickup";

export class PickupService extends RestServiceConnection {

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE LA LISTA DE RECOGIDAS
     * @EN GETS THE LIST OF PICKUPS
     */
    // -------------------------------------------------------------------------------------------------------------
    listPickups = async (filters?: any) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/list",
            data: filters,
        }, true) as AxiosResponse;
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL RECUENTO DE PRODUCTOS SUGERIDOS EN UNA RECOGIDA
     * @EN GETS THE COUNT OF SUGGESTED PRODUCTS IN A PICKUP
     */
    // -------------------------------------------------------------------------------------------------------------
    getPickupSuggestedProductsQuantity = async (headquarters: string[]) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/suggested-products-quantity",
            data: { headquarters },
        }, true) as AxiosResponse;
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTAR LA CANTIDAD DE PRODUCTOS SUGERIDOS EN UNA RECOGIDA
     * @EN EXPORT THE COUNT OF SUGGESTED PRODUCTS IN A PICKUP
     */
    // -------------------------------------------------------------------------------------------------------------
    exportPickupSuggestedProductsQuantity = async (headquarters: string[]) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/export-suggested-products-quantity",
            data: { headquarters },
            responseType: "blob",
        }, true) as AxiosResponse;
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL RECUENTO DE RECOGIDAS POR SU ESTADO
     * @EN GETS THE COUNT OF PICKUPS BY THEIR STATUS
     */
    // -------------------------------------------------------------------------------------------------------------
    getPickupCountByStatus = async (filters?: any) => {
        this.response = await this.makeRequest({
            method: "GET",
            url: PICKUPS_ENDPOINT + "/pickups-by-status",
            data: filters,
        }, true) as AxiosResponse;
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE UN RECOGIDA POR SU ID
     * @EN GETS A PICKUP BY ITS ID     
     */
    // -------------------------------------------------------------------------------------------------------------
    getPickupById = async (pickupId: string) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/get",
            data: { id: pickupId },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES Obtiene las recogidas asignadas a un conductor por su ID
     * @EN Gets the pickups assigned to a driver by their ID
     */
    // -------------------------------------------------------------------------------------------------------------
    getDriverAssignedPickups = async (filters?: any) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/driver-assigned-pickups",
            data: {
                date: filters.filter_filters.pickupDate
            },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES TOGGLEA UN RECOGIDA
     * @EN TOGGLES A PICKUP
     */
    // -------------------------------------------------------------------------------------------------------------
    changePickupStatus = async (pickupId: string, status: PickupStatus) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/toggle",
            data: { pickupId: pickupId, status: status },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES FIRMA EL RECIBO DE UNA RECOGIDA
     * @EN SIGNS A PICKUP RECEIPT
     */
    // -------------------------------------------------------------------------------------------------------------
    signReceipt = async (id: string, signatureFile: File) => {
        const form = new FormData();
        form.append('id', id);
        form.append('signature', signatureFile, signatureFile.name);

        this.response = await this.makeRequest({
            method: 'POST',
            url: PICKUPS_ENDPOINT + '/sign-receipt', // usa '/pickup/sign-receipt' si tu ruta lleva prefijo
            data: form,
            // MUY IMPORTANTE: no fuerces application/json.
            // Si tu RestServiceConnection mete por defecto JSON, añade esto:
            headers: { /* vacío a propósito */ },
            // Evita transformaciones que "stringifiquen" el cuerpo:
            transformRequest: [(d) => d as any],
        }, true);

        return this;
    }
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREA UN RECOGIDA
     * @EN CREATES A PICKUP
     */
    // -------------------------------------------------------------------------------------------------------------
    createPickup = async (pickup: any) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/create",
            data: pickup,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITA UN RECOGIDA
     * @EN EDITS A PICKUP
     */
    // -------------------------------------------------------------------------------------------------------------
    editPickup = async (pickup: any) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/edit",
            data: pickup,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA UN RECOGIDA
     * @EN DELETES A PICKUP
     */
    // -------------------------------------------------------------------------------------------------------------
    deletePickup = async (pickupId: string) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/delete",
            data: {
                ids: [pickupId]
            },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA MÚLTIPLES RECOGIDAS
     * @EN DELETES MULTIPLE PICKUPS
     */
    // -------------------------------------------------------------------------------------------------------------
    deleteMultiPickups = async (ids: string[]) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/delete",
            data: { ids },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTA RECOGIDAS A EXCEL
     * @EN EXPORTS PICKUPS TO EXCEL
     */
    // -------------------------------------------------------------------------------------------------------------
    exportToExcel = async (filters?: any) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/export-to-excel",
            data: filters,
            responseType: "blob",
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTA RECOGIDAS A PDF
     * @EN EXPORTS PICKUPS TO PDF
     */
    // -------------------------------------------------------------------------------------------------------------
    exportToPDF = async (filters?: any) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/export-pdf",
            data: filters,
            responseType: "blob",
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES Genera el ticket de la recogida
     *
     * @EN Generates the pickup ticket
     */
    // -------------------------------------------------------------------------------------------------------------
    generateTicket = async (pickupId: string) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/generate-ticket",
            data: {
                id: pickupId
            },
            responseType: "blob",
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES Genera el recibo de la recogida
     *
     * @EN Generates the pickup receipt
     *
     * @param id
     */
    // -------------------------------------------------------------------------------------------------------------
    generateReceipt = async (pickupId: string) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/generate-receipt",
            data: {
                id: pickupId
            },
            responseType: "blob",
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES EJECUTA UNA RECOGIDA (MARCA COMO COMPLETADA)
     * @EN EXECUTES A PICKUP (MARKS AS COMPLETED)
     */
    // -------------------------------------------------------------------------------------------------------------
    executePickup = async (data: any) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/execute",
            data,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MARCA UNA RECOGIDA COMO FALLIDA Y REPROGRAMA UNA NUEVA FECHA
     * @EN MARKS A PICKUP AS FAILED AND SCHEDULES A NEW DATE
     */
    // -------------------------------------------------------------------------------------------------------------
    failPickup = async (data: any) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/mark-failed",
            data,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES REPROGRAMA UNA O VARIAS RECOGIDAS MANUALMENTE
     * @EN MANUALLY RESCHEDULES ONE OR MULTIPLE PICKUPS
     */
    // -------------------------------------------------------------------------------------------------------------
    reschedulePickups = async (data: { ids: string[], newDate: string, rescheduleReason?: string }) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/reschedule",
            data,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL PRIMER VEHÍCULO USADO POR EL CONDUCTOR EN EL DÍA (SUGERENCIA)
     * @EN GETS DRIVER'S FIRST VEHICLE OF THE DAY (SUGGESTION)
     */
    // -------------------------------------------------------------------------------------------------------------
    getDriverFirstVehicleOfDay = async (driverId?: string | null) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/driver-first-vehicle-of-day",
            data: { driverId },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES ACTUALIZA EL VEHÍCULO DIARIO DEL CONDUCTOR
     * @EN UPDATES DRIVER'S DAILY VEHICLE
     */
    // -------------------------------------------------------------------------------------------------------------
    updateDriverDailyVehicle = async (driverId: string | null | undefined, vehicleId: string) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/update-driver-daily-vehicle",
            data: { driverId, vehicleId },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * ES: Obtiene el PDF del albarán (recibo) a partir de un hash público.
     * EN: Fetches the receipt PDF by public hash.
     */
    // -------------------------------------------------------------------------------------------------------------
    showReceipt = async (hash: string) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: '/show-receipt',
            data: { hash },
            headers: { "Content-Type": "application/json" },
            responseType: "blob",
        }, false, true) as AxiosResponse;
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES ACEPTA UNA RECOGIDA SUGERIDA Y LA ASIGNA A UNA RUTA
     * @EN ACCEPTS A SUGGESTED PICKUP AND ASSIGNS IT TO A ROUTE
     */
    // -------------------------------------------------------------------------------------------------------------
    acceptSuggestedPickup = async (data: { pickupId: string, routeId: string }) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/accept-suggested-pickup",
            data,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES ASIGNA UNA RECOGIDA CERCANA AL CONDUCTOR ACTUAL
     * @EN ASSIGNS A NEARBY PICKUP TO THE CURRENT DRIVER
     */
    // -------------------------------------------------------------------------------------------------------------
    assignPickup = async (data: { pickupId: string, userId: string, assignedAt: string, originalTime: string }) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/assign",
            data,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES RECHAZA UNA RECOGIDA SUGERIDA CON UN MOTIVO OPCIONAL
     * @EN REJECTS A SUGGESTED PICKUP WITH AN OPTIONAL REASON
     */
    // -------------------------------------------------------------------------------------------------------------
    rejectSuggestedPickup = async (data: { pickupId: string, rejectionReason?: string }) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/reject-suggested",
            data,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE LA LISTA DE RECOGIDAS PARA UN SELECT
     * @EN GETS THE LIST OF PICKUPS FOR A SELECT   
     */
    // -------------------------------------------------------------------------------------------------------------
    // TODO: develop it in backend
    // listForSelect = async (filters?: any) => {
    //     this.response = await this.makeRequest({
    //         method: "POST",
    //         url: PICKUPS_ENDPOINT + "/list-select",
    //         data: filters,
    //     }, true) as AxiosResponse;
    //     return this;
    // }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES INICIA LAS RECOGIDAS DIARIAS
     * @EN STARTS DAILY PICKUPS
     */
    // -------------------------------------------------------------------------------------------------------------
    startDailyPickups = async (data?: any) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/start-daily-pickups",
            data,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES FINALIZA LAS RECOGIDAS DIARIAS
     * @EN FINISHES DAILY PICKUPS
     */
    // -------------------------------------------------------------------------------------------------------------
    finishDailyPickups = async (data?: any) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/finish-daily-pickups",
            data,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE LAS SEDES ASOCIADAS A UNA RUTA
     * @EN GETS HEADQUARTERS ASSOCIATED WITH A ROUTE
     */
    // -------------------------------------------------------------------------------------------------------------
    getRouteHeadquarters = async (routeId: string) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/route-headquarters",
            data: { routeId },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE LA VALIDACIÓN ADMINISTRATIVA DE UNA RUTA
     * @EN GETS ADMINISTRATIVE VALIDATION FOR A ROUTE
     */
    // -------------------------------------------------------------------------------------------------------------
    adminValidation = async (values: any) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/admin-validation",
            data: values,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES IMPORTA RECOGIDAS DESDE UN EXCEL
     * @EN IMPORTS PICKUPS FROM AN EXCEL FILE
     */
    // -------------------------------------------------------------------------------------------------------------
    importExcel = async (formData: FormData) => {
        this.response = await this.makeRequest({
            method: "POST",
            url: PICKUPS_ENDPOINT + "/import",
            data: formData,
            headers: { "Content-Type": "multipart/form-data" }
        }, true);
        return this;
    }
    // -------------------------------------------------------------------------------------------------------------
}